import { useMemo } from "react";
import cx from "classnames";
import { Flex, Text, ScrollArea, Avatar } from "@radix-ui/themes";
import * as Tabs from "@radix-ui/react-tabs";
import { RadixColor } from "@/components/radixColors";
import styles from "./Evaluation.styles.module.scss";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { QaScore } from "@/v2/entities/qa-score/QaScore.model";

export interface EvaluationTabItem {
  value: string;
  title: string;
  color: RadixColor;
  publicCommentsCount?: number;
  avgQaScore?: number;
  totalAdjusted?: number;
  scores: QaScore[];
}

function EvaluationTab({
  value,
  title,
  color,
  publicCommentsCount,
  avgQaScore,
}: EvaluationTabItem) {
  const initials = useMemo(() => {
    const words = title.split(" ").map((x) => x[0]);
    return words.length === 1
      ? words[0]
      : [words[0], words[words.length - 1]].join("");
  }, [title]);

  const hasAvatar = value !== "ticket" && value !== "conversation";

  return (
    <Tabs.Trigger
      key={value}
      className={cx(styles.Trigger, {
        [styles.hasAvatar]: hasAvatar,
      })}
      value={value}
    >
      <Flex width={"100%"} maxWidth={"100%"} gap={"3"} align={"center"}>
        {hasAvatar && (
          <Avatar
            size={"2"}
            fallback={initials}
            color={color}
            radius={"full"}
          />
        )}

        <Flex
          width={"100%"}
          maxWidth={"100%"}
          direction={"column"}
          align={"start"}
        >
          <Flex
            width={"100%"}
            maxWidth={"100%"}
            gap={"4"}
            justify={"between"}
            align={"center"}
          >
            <Text
              size={"2"}
              color={"gray"}
              weight={"medium"}
              highContrast
              truncate
            >
              {title}
            </Text>

            {avgQaScore != null && (
              <Text size={"2"}>
                <AvgQaScoreFormatter value={avgQaScore} />
              </Text>
            )}
          </Flex>

          {(publicCommentsCount ?? 0) > 0 && (
            <Text size={"2"} color={"gray"}>
              {publicCommentsCount} Public Messages
            </Text>
          )}
        </Flex>
      </Flex>
    </Tabs.Trigger>
  );
}

interface EvaluationTabsProps {
  tabs: EvaluationTabItem[];
  value: string;
  onChange: (value: string) => void;
}

export function EvaluationTabs({ tabs, value, onChange }: EvaluationTabsProps) {
  return (
    <Tabs.Root
      className={styles.EvaluationTabs}
      value={value}
      onValueChange={onChange}
    >
      <ScrollArea scrollbars={"horizontal"}>
        <Tabs.List className={styles.List}>
          {tabs.map((tab) => (
            <EvaluationTab key={tab.value} {...tab} />
          ))}
        </Tabs.List>
      </ScrollArea>
    </Tabs.Root>
  );
}
